<!--*
 * Add new room
 *
 * @author   mauricio.figueroa
 * @date     2020-07-17
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <div v-if="!showInput" class="card h-100 shadow-card">
    <div class="mx-auto my-auto">
      <app-button
        :disabled="disabled"
        size="lg"
        theme="primary"
        btn-type="ghost"
        icon="plus-circle"
        layout="icon-label"
        @click="toggleInput"
      >
        {{
          recurrent
            ? $t('siteDetails.alreadyThere.places.addRecurrentRoom')
            : $t('siteDetails.alreadyThere.places.addRoom')
        }}
      </app-button>
    </div>
  </div>
  <div v-else class="card p-5 shadow--sm">
    <div class="container">
      <div class="row">
        <div class="font-weight-normal h5">
          {{$t('siteDetails.addRoom.title')}}
        </div>
      </div>
      <div class="row">
        <smtt-input @inputValueChange="onValueChanged" :label="$t('siteDetails.addRoom.name')" full-width/>
      </div>
      <div class="row">
        <smtt-input @inputValueChange="onDescription" :label="$t('siteDetails.addRoom.description')" full-width/>
      </div>
      <check-button
      :label="$t('siteDetails.addRoom.template')"
      :items="checkButtonItems"
      @selected="onItemSelected"
      />
      <div class="row">
        <div class="col p-0">
          <div class="text-right">
            <div class="row">
              <div class="col">
                <span class="pr-5">
                  <app-button
                    btn-type="outline"
                    @click="toggleInput">
                    {{ $t('siteDetails.addRoom.dismissBtn') }}
                  </app-button>
                </span>
                <span>
                  <app-button
                    :disabled="!addRoomBtn"
                    @click="onAddRoom">
                    {{ $t('siteDetails.addRoom.addBtn') }}
                  </app-button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import CheckButton from '@/components/CheckButton'
import AppButton from '@/components/AppButton';

export default {
  'name': 'AddRoom',
  'props': [ 'disabled', 'recurrent', 'surveyTemplates' ],
  'components': { CheckButton, AppButton },
  data() {
    return {
      'showInput': false,
      'addRoomBtn': false,
      'roomName': '',
      'roomDescription': '',
      'checkButtonItems': [],
    }
  },
  'watch': {
    disabled(newValue) {
      this.disabled = newValue;
    },
    surveyTemplates(newValue) {
      this.formatSurveyTemplates(newValue);
    }
  },
  'methods': {
    ...mapActions([ 'addRoom', 'getRooms' ]),
    toggleInput() {
      this.showInput = !this.showInput;
      this.$emit('inputChanged', false);
      this.roomName = '';
      this.roomDescription = '';
      this.addRoomBtn = false;
      this.checkButtonItems = this.checkButtonItems.map((item) => {
        // If there's only one survey template, check it for default.
        if (this.checkButtonItems.length > 1) {
          item.checked = false;
        } else {
          item.checked = true;
        }

        return item;
      });
    },
    formatSurveyTemplates(templates) {
      this.checkButtonItems = templates.map((item) => {
        return {
          id: item.surveyTemplateId,
          label: item.surveyName,
          checked: templates.length > 1 ? false : true, // If there's only one survey template, check it for default.
        }
      });
    },
    onValueChanged(e) {
      this.roomName = e.detail.value;
      const valid = this.isFieldValid();
      this.addRoomBtn = valid;
      this.$emit('inputChanged', valid);

    },
    onDescription(e) {
      this.roomDescription = e.detail.value;
    },
    isFieldValid() {
      const roomNameValid = this.roomName.trim().length > 0;
      const surveyTemplateValid = this.checkButtonItems.some((item) => item.checked);

      return roomNameValid && surveyTemplateValid;
    },
    onAddRoom() {
      if (this.addRoomBtn) {
        const newRoom = {
          'assignmentId': parseInt(this.$route.params.assignmentId),
          'roomName': this.roomName,
          'roomDescription': this.roomDescription.trim() !== '' ? this.roomDescription : null,
          'isRecurrentRoom': !!this.recurrent,
          'surveyTemplates': this.checkButtonItems.filter((item) => item.checked).map((item) => item.id),
        };
        this.addRoom(newRoom).then((response) => {
          this.$emit('newRoomAdded', response);
          this.toggleInput();
        })
      }
    },
    onItemSelected(id) {
      this.checkButtonItems = this.checkButtonItems.map((item) => {
        if (item.id === id) {
          item.checked = !item.checked;
        }

        return item;
      });

      const valid = this.isFieldValid();
      this.addRoomBtn = valid;
      this.$emit('inputChanged', valid);
    }
  },
  created() {
    this.formatSurveyTemplates(this.surveyTemplates);
  }
}
</script>

<style scoped>
  .card {
    min-height: 100px;
  }
</style>